import { GameObjectManager } from "../GameObjectManager";
import { ResizeNode } from "./ResizeNode";
import { FrontViewManager } from "../FrontViewManager";
import { PlanManager } from "../../../../cocosPlan";
import { ComponentConstants, Events, eventsManager, math, SavaneMath, SceneConstants } from "@rhinov/savane-js";
import { getMediaArrangementUrl, getMediaUrl } from "@rhinov/asset-manager-service";

declare var cc: any;

export class FrontViewNode extends cc.DrawNode {

    constructor(obj: any, scale: number) {
        super();

        this.setName('deco' + obj.id);
        this._isHovered = false;
        this.selected = false;
        this.valid = true;

        this._obj = obj;
        if (this._obj.isJoineryEntity() || this._obj.isTechnicalElementEntity()) {
            this.resizeMenu = new ResizeNode(this._obj, true, scale);
            this.addChild(this.resizeMenu, 8);
        }
        this._spritePreview = null;
        this._displayName = null;

        if (obj.isArrangementObjectEntity()) {
            var arrangementAssetId = obj._objectId;
            var url = getMediaArrangementUrl() +
                arrangementAssetId +
                '/medias/previews/preview1-sm.webp';

            this._spritePreview = new cc.Sprite(url);
            this._spritePreview.addLoadedEventListener(function(this: FrontViewNode) {
                var scale = PlanManager.getInstance().frontViewLayer._scale!;
                var spriteScale = Math.min(this._obj.length * scale / this._spritePreview!.width,
                    this._obj.height * scale / this._spritePreview!.height);
                this._spritePreview!.setScale(spriteScale, spriteScale);
                this.addChild(this._spritePreview!);
            }, this);
        }
        else {
            if (obj.isJoineryEntity()) {
                var component = obj.getComponent(ComponentConstants.ComponentType.JoineryType);

                if (component !== null) {
                    var joineryAssetId = component.joineryTypeId;
                    var url = getMediaUrl() +
                        'joineries/' +
                        joineryAssetId +
                        '/medias/previews/preview1-sm.webp';

                    this._spritePreview = new cc.Sprite(url);
                    this._spritePreview.addLoadedEventListener(function(this: FrontViewNode) {
                        this._spritePreview!.setAnchorPoint(new cc.p(0.5, 1));
                        if (this._obj.isJoineryEntity() && this._obj.joineryType === SceneConstants.JoineryType.velux) {
                            this._spritePreview!.setPosition(new cc.p(0, (this._obj.getVerticalHeight() / 4) * scale - 10))
                        }
                        else {
                            this._spritePreview!.setPosition(new cc.p(0, (this._obj.height / 6) * scale - 10));
                        }

                        this.addChild(this._spritePreview!);
                    }, this);
                }
            }

            if (obj.isTechnicalElementEntity()) {
                var component = obj.getComponent(ComponentConstants.ComponentType.TechnicalElementType);

                if (component !== null) {
                    var technicalAssetId = component.technicalElementTypeId;
                    var url = getMediaUrl() +
                        'technicalelements/' +
                        technicalAssetId +
                        '/medias/previews/preview1-sm.webp';

                    this._spritePreview = new cc.Sprite(url);
                    this._spritePreview.addLoadedEventListener(function(this: FrontViewNode) {
                        this._spritePreview!.setAnchorPoint(new cc.p(0.5, 1));
                        this._spritePreview!.setPosition(new cc.p(0, (this._obj.height / 6) * scale - 10));

                        this.addChild(this._spritePreview!);
                    }, this);
                }
            }

            this._displayName = FrontViewManager.generateName(obj);
            this._displayNameLabel = new cc.LabelTTF(this._displayName, 'Roboto-Light', 13, new cc.size(0, 18), cc.TEXT_ALIGNMENT_CENTER, cc.TEXT_ALIGNMENT_CENTER);
            this._displayNameLabel.setName('TopLabel');
            if (this._obj.isJoineryEntity() && this._obj.joineryType === SceneConstants.JoineryType.velux) {
                this._displayNameLabel.setPosition(new cc.p(0, (this._obj.getVerticalHeight() / 4) * scale));
            }
            else {
                this._displayNameLabel.setPosition(new cc.p(0, (this._obj.height / 6) * scale));
            }
            this._displayNameLabel.setAnchorPoint(new cc.p(0.5, 0.5));
            this._displayNameLabel.setColor(new cc.color(0, 0, 0));
            this._displayNameLabel.setFontFillColor(new cc.color(0, 0, 0));
            this.addChild(this._displayNameLabel);
        }

        this.hideArrangement = eventsManager.instance.addListener(Events.HIDE_ARRANGEMENTS, function(this: FrontViewNode, event: any) {
            if (this._obj.isArrangementObjectEntity()) {
                this.setVisible(!PlanManager.getInstance()._hideArrangements);
            }
        }.bind(this));
    }

    update(scale: number) {
        var dist;

        if (this._spritePreview !== null) {
            var spriteScale = Math.min((this._obj.length * 0.9) * scale / this._spritePreview.width, (this._obj.height / 2) * scale / this._spritePreview.height);

            if (this._obj.isJoineryEntity() && this._obj.joineryType === SceneConstants.JoineryType.velux) {
                var veluxHeight = this._obj.getVerticalHeight();
                spriteScale = Math.min((this._obj.length * 0.9) * scale / this._spritePreview.width, (veluxHeight / 2) * scale / this._spritePreview.height);
            }

            this._spritePreview.setScale(spriteScale, spriteScale);
        }

        var wall = PlanManager.getInstance().frontViewLayer.currentWall;
        var bboxcut = wall?.boundingBoxCut ?? [];
        var realLength = PlanManager.getInstance().frontViewLayer.computeWallLength();
        if (this._obj.isJoineryEntity()) {
            var objPosition = SavaneMath.getProjection(this._obj.position, wall!.begin, wall!.end);
            if (PlanManager.getInstance().frontViewLayer.isDirectSide) {
                var d = math.vec2.create();
                math.vec2.subtract(d, objPosition, bboxcut[1]);
                dist = math.vec2.length(d) * scale;
            }
            else {
                var d = math.vec2.create();
                math.vec2.subtract(d, objPosition, bboxcut[0]);
                dist = (realLength - math.vec2.length(d)) * scale;
            }

            if (this._obj.joineryType === SceneConstants.JoineryType.velux) {
                this.setPosition(new cc.p(dist, this._obj.position[2] * scale));
            }
            else {
                this.setPosition(new cc.p(dist, (this._obj.floorHeight + this._obj.height / 2) * scale));
            }

            this.resizeMenu.setVisible(this.selected);
            if (this.selected) {
                this.resizeMenu.update(scale);
            }
        }
        else {
            var point1 = math.vec3.clone(this._obj.position);
            var point2 = math.vec3.create();
            point1[2] = wall!.begin[2];
            math.vec3.set(point2,
                point1[0] + wall!.wallDirection[1] * 100,
                point1[1] + wall!.wallDirection[0] * 100,
                wall!.begin[2])

            var projOnWall = SavaneMath.getCrossPoint(point1, point2, wall!.begin, wall!.end);

            if (projOnWall) {
                if (PlanManager.getInstance().frontViewLayer.isDirectSide) {
                    dist = (math.vec3.dist(projOnWall, bboxcut[1])) * scale;
                }
                else {
                    dist = (realLength - math.vec3.dist(projOnWall, bboxcut[0])) * scale;
                }
                if (this._obj.isTechnicalElementEntity()) {
                    this.setPosition(new cc.p(dist, (this._obj.floorHeight + this._obj.height / 2) * scale));

                    this.resizeMenu.setVisible(this.selected);
                    if (this.selected) {
                        this.resizeMenu.update(scale);
                    }
                }
                else {
                    this.setPosition(new cc.p(dist, (this._obj.position[2] - wall!.begin[2]) * scale));
                }
            }
        }

        var color = this.valid ? (this.selected ?
            new cc.color('#F5D231') : new cc.color('#555b62'))
            : new cc.color('#FF7D8D');

        this.clear();

        var objHeight = this._obj.height

        if (this._obj.isJoineryEntity() && this._obj.joineryType === SceneConstants.JoineryType.velux) {
            objHeight = this._obj.getVerticalHeight();
        }

        // Display nbDoors or nbCasements
        var nbRect = this._obj.nbDoors;
        if (nbRect === undefined) {
            nbRect = this._obj.nbCasement;
        }

        if (nbRect !== undefined) {
            var rectColor = new cc.color('#C8CACC');

            for (var i = 0; i < nbRect; i++) {
                var width = this._obj.length / nbRect;
                var xPos = (-this._obj.length / 2) + (i * width);
                this.drawRect(
                    new cc.p(xPos * scale, -objHeight / 2 * scale),
                    new cc.p((xPos + width) * scale, objHeight / 2 * scale),
                    new cc.color(255, 255, 255, 0), 4, rectColor);
            }
        }

        this.drawRect(
            new cc.p(-this._obj.length / 2 * scale, -objHeight / 2 * scale),
            new cc.p(this._obj.length / 2 * scale, objHeight / 2 * scale),
            new cc.color(255, 255, 255, 0), 4, color);

        if (this._obj.transom) {
            this.drawRect(
                new cc.p(-this._obj.length / 2 * scale, ((objHeight / 2) + (this._obj.transomHeight - (this._obj.floorHeight + this._obj.height))) * scale),
                new cc.p(this._obj.length / 2 * scale, objHeight / 2 * scale),
                new cc.color(255, 255, 255), 4, color);
        }

        if (this._obj.bottomTransom) {
            this.drawRect(
                new cc.p(-this._obj.length / 2 * scale, -objHeight / 2 * scale),
                new cc.p(this._obj.length / 2 * scale, ((-objHeight / 2) - (this._obj.floorHeight - this._obj.bottomTransomHeight)) * scale),
                new cc.color(255, 255, 255), 4, color);
        }

        if (this.selected) {
            var objFloorHeight;

            if (this._obj.isJoineryEntity() && this._obj.joineryType === SceneConstants.JoineryType.velux) {
                objFloorHeight = this._obj.position[2] - (objHeight / 2);
            }
            else {
                objFloorHeight = this._obj.floorHeight;
            }

            GameObjectManager.createDimension('DimLeftDeco', '',
                new cc.p(-this._obj.length / 2 * scale, -objHeight / 2 * scale),
                new cc.p(-this._obj.length / 2 * scale, objHeight / 2 * scale),
                new cc.p(1, 0), 20, scale * 10, true, this);
            GameObjectManager.createDimension('DimTopDeco', '',
                new cc.p(-this._obj.length / 2 * scale, -objHeight / 2 * scale),
                new cc.p(+this._obj.length / 2 * scale, -objHeight / 2 * scale),
                new cc.p(0, 1), 20, scale * 10, true, this);

            GameObjectManager.createDimension('DimBottomWallDeco', '',
                new cc.p(0, -objHeight / 2 * scale),
                new cc.p(0, (-objHeight / 2 - objFloorHeight) * scale),
                new cc.p(1, 0), 0, scale * 10, true, this);
            GameObjectManager.createDimension('DimTopWallDeco', '',
                new cc.p(0, (objHeight / 2) * scale),
                new cc.p(0, ((objHeight / 2) + (PlanManager.getInstance().frontViewLayer.currentWall?.height! - objFloorHeight - objHeight)) * scale),
                new cc.p(1, 0), 0, scale * 10, true, this);

            GameObjectManager.createDimension('DimLeftWallDeco', '',
                new cc.p((-this._obj.length / 2) * scale, 0),
                new cc.p((-(dist! / scale)) * scale, 0),
                new cc.p(1, 0), 0, scale * 10, true, this);
            GameObjectManager.createDimension('DimRightWallDeco', '',
                new cc.p((this._obj.length / 2) * scale, 0),
                new cc.p((realLength - (dist! / scale)) * scale, 0),
                new cc.p(1, 0), 0, scale * 10, true, this);
        }
        else {
            GameObjectManager.deleteDimension('DimLeftDeco', true, this);
            GameObjectManager.deleteDimension('DimTopDeco', true, this);

            GameObjectManager.deleteDimension('DimLeftWallDeco', true, this);
            GameObjectManager.deleteDimension('DimRightWallDeco', true, this);
            GameObjectManager.deleteDimension('DimTopWallDeco', true, this);
            GameObjectManager.deleteDimension('DimBottomWallDeco', true, this);
        }
    }

    toResize(position: math.vec3, scale: number) {
        return (this._obj.isJoineryEntity() || this._obj.isTechnicalElementEntity()) ? this.resizeMenu.toResize(position, scale) : false;
    }

    getZoomScale() {
        return 1;
    }

    onExit() {
        super.onExit();
        eventsManager.instance.removeListener(this.hideArrangement);
    }
}
